// ─────────────────────────────────────────────
//  ImportReviewQueue — the coverage report, and the questions behind it.
//
//  Anesthesia Team Control (ATC)
//  Copyright © 2026 Gonzalo Martinez (gamc00@gmail.com). All rights reserved.
//  Proprietary and confidential. Not licensed for public use — see /LICENSE.
// ─────────────────────────────────────────────
//
//  WHAT THIS REPLACES. `NewStaffPanel` listed the names an import could not
//  place under the heading "New staff to add", every box ticked by default, and
//  Apply created them. On the live sheet of 2026-08-20 that panel would have
//  offered 22 boxes — 13 of them colleagues who were already in the roster
//  under their legal name, and one of them a note ("to OB floor"). That default
//  is where the duplicate colleagues came from.
//
//  ⚠️ NOTHING HERE IS TICKED FOR YOU. Every action is a click a named human
//  makes, and the two kinds of question get different words and different
//  default actions, because they are different problems:
//
//    · SPELLING  — somebody on file is one transposition away. The default is
//      "yes, this is them"; confirming writes a `staff_aliases` record and the
//      same cell resolves silently every morning after.
//    · NEW PERSON — nobody on file resembles this name. The default is SKIP.
//      Adding them is a second, separate click with a confirm, because the
//      failure this whole feature exists to remove is a wrong add.
//
//  ⚠️ AND A HUMAN MAY NAME SOMEBODY THE LADDER DID NOT OFFER. Until 2026-09-07
//  the only people bindable here were the ones `resolveIdentity` proposed, so a
//  name it could place NOBODY against — `Liz B`, `Shannon M` — offered exactly
//  two actions: Skip, or "add as a new person". The owner knows perfectly well
//  that Liz B is Elizabeth Bennett (already on file, with a login); the queue
//  had nowhere to put that. Worse, the one available button would have created
//  a SECOND phantom and written an alias binding the cell to it forever — a
//  duplicate that looks resolved. The roster picker is how an owner's own
//  knowledge gets in, which is the entire point of rung 1.
//
//  ⚠️ THE REPORT IS THE GATE. You cannot apply an import that still has open
//  questions without saying you have read them — the nightly job full-resets
//  each board, so an unseen refusal rebuilds the day without that person.
// ─────────────────────────────────────────────
import React, { useState } from "react";
import tokens from "../theme/tokens";
import { REVIEW_KINDS, coverageLine } from "../features/sheet-import/importCoverage";

const { color, space, radius } = tokens;

const KIND_LABEL = {
  [REVIEW_KINDS.SPELLING]: "Spelled differently",
  [REVIEW_KINDS.NEW_PERSON]: "Not on file",
  [REVIEW_KINDS.AMBIGUOUS]: "More than one match",
  [REVIEW_KINDS.UNREADABLE]: "Cell not understood",
};

/**
 * @param {object} props
 *   @param {object} props.coverage   sumCoverage(...) — { named, placed, needsYou, unreadable, items }
 *   @param {(item, candidate) => Promise} [props.onConfirm]  writes the alias
 *   @param {(item) => Promise} [props.onCreatePerson]        the deliberate add
 *   @param {(item) => void} [props.onSkip]
 *   @param {object} [props.resolvedIds]  { [itemId]: "confirmed"|"created"|"skipped" }
 *   @param {Array<{id:string, name:string}>} [props.staffOptions]  the whole
 *     roster, so a reviewer can bind a cell to somebody the ladder never
 *     proposed. Without it, a name with no candidates can only be skipped or
 *     duplicated.
 *   @param {boolean} [props.acknowledged]
 *   @param {(v:boolean) => void} [props.onAcknowledge]
 */
export default function ImportReviewQueue({
  coverage,
  onConfirm,
  onCreatePerson,
  onSkip,
  resolvedIds = {},
  acknowledged = false,
  onAcknowledge,
  staffOptions = [],
}) {
  const cov = coverage || { named: 0, placed: 0, needsYou: 0, unreadable: 0, items: [] };
  const items = cov.items || [];
  const open = items.filter((i) => !resolvedIds[i.id]);

  return (
    <section style={st.panel} aria-label="Import coverage and review queue">
      {/* THE ONE LINE. Same sentence the nightly job logs and stores. */}
      <div style={st.headline} role="status">
        {coverageLine(cov)}
      </div>
      <p style={st.note}>
        Everything counted as <strong>placed</strong> lands on the board with a staff
        record — messageable, moveable, breakable. The rest is below. Nothing here
        is created for you.
      </p>

      {(cov.unknownDoctorCodes || []).length > 0 && (
        <p style={st.note}>
          Attending codes no doctor record carries:{" "}
          <strong>{cov.unknownDoctorCodes.join(", ")}</strong>. Their rooms keep an
          unresolved attending — add the short code in Setup ▸ Users.
        </p>
      )}

      {open.length === 0 && items.length > 0 && (
        <div style={st.allClear}>All {items.length} question{items.length === 1 ? "" : "s"} answered.</div>
      )}

      <ul style={st.list}>
        {items.map((item) => (
          <QueueItem
            key={item.id}
            item={item}
            state={resolvedIds[item.id] || ""}
            onConfirm={onConfirm}
            onCreatePerson={onCreatePerson}
            onSkip={onSkip}
            staffOptions={staffOptions}
          />
        ))}
      </ul>

      {(cov.needsYou > 0 || cov.unreadable > 0) && (
        <label style={st.ack}>
          <input
            type="checkbox"
            checked={acknowledged}
            onChange={(e) => onAcknowledge && onAcknowledge(e.target.checked)}
          />
          <span>
            I have read the {cov.needsYou} unidentified {cov.needsYou === 1 ? "person" : "people"}
            {cov.unreadable ? ` and the ${cov.unreadable} unreadable cell${cov.unreadable === 1 ? "" : "s"}` : ""}.
          </span>
        </label>
      )}
    </section>
  );
}

function QueueItem({ item, state, onConfirm, onCreatePerson, onSkip, staffOptions = [] }) {
  const [confirmingCreate, setConfirmingCreate] = useState(false);
  const [picking, setPicking] = useState(false);
  const [picked, setPicked] = useState("");
  const [busy, setBusy] = useState(false);
  const top = item.candidates?.[0] || null;
  // Everyone the ladder did NOT already offer — showing a name twice in one
  // list makes the reviewer wonder which one is different.
  const offered = new Set((item.candidates || []).map((c) => c.staffId));
  const others = staffOptions.filter((p) => p && p.id && p.name && !offered.has(p.id));
  const selectId = `pick-${item.id}`;

  const run = async (fn) => {
    if (!fn) return;
    setBusy(true);
    try { await fn(); } finally { setBusy(false); }
  };

  return (
    <li style={{ ...st.item, opacity: state ? 0.55 : 1 }}>
      <div style={st.itemHead}>
        <span style={st.kind}>{KIND_LABEL[item.kind] || item.kind}</span>
        <span style={st.where}>
          {item.room || "no room"}{item.sheetRow ? ` · sheet row ${item.sheetRow}` : ""}
        </span>
        {state && <span style={st.state}>{state}</span>}
      </div>

      {/* THE CELL, EXACTLY AS THE DEPARTMENT WROTE IT. */}
      <code style={st.verbatim}>{item.verbatim}</code>

      <div style={st.understood}>Read as: {item.understood}</div>
      <div style={st.why}>{item.why}</div>

      {!state && (
        <div style={st.actions}>
          {item.kind !== REVIEW_KINDS.UNREADABLE && item.candidates?.length > 0 && (
            item.candidates.map((c) => (
              <button
                key={c.staffId}
                type="button"
                disabled={busy}
                style={c === top && item.defaultAction === "confirm" ? st.primaryBtn : st.secondaryBtn}
                onClick={() => run(() => onConfirm && onConfirm(item, c))}
              >
                This is {c.name}
              </button>
            ))
          )}

          {/* ⚠️ BINDING TO SOMEBODY ALREADY ON FILE IS THE SAFE ANSWER, so it
              sits with the other confirmations and BEFORE Skip. It writes the
              same alias the proposed candidates do; the only difference is who
              chose the name. */}
          {item.kind !== REVIEW_KINDS.UNREADABLE && others.length > 0 && (
            picking ? (
              <span style={st.confirmWrap}>
                <label htmlFor={selectId} style={st.pickLabel}>
                  {item.name} is
                </label>
                <select
                  id={selectId}
                  style={st.picker}
                  value={picked}
                  disabled={busy}
                  onChange={(e) => setPicked(e.target.value)}
                >
                  <option value="">Choose someone…</option>
                  {others.map((p) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
                <button
                  type="button"
                  disabled={busy || !picked}
                  style={st.secondaryBtn}
                  onClick={() => {
                    const hit = others.find((p) => p.id === picked);
                    if (hit) run(() => onConfirm && onConfirm(item, { staffId: hit.id, name: hit.name }));
                  }}
                >
                  Confirm
                </button>
                <button type="button" disabled={busy} style={st.secondaryBtn} onClick={() => setPicking(false)}>
                  Cancel
                </button>
              </span>
            ) : (
              <button type="button" disabled={busy} style={st.secondaryBtn} onClick={() => setPicking(true)}>
                {item.candidates?.length ? "Someone else…" : "Choose from the roster…"}
              </button>
            )
          )}

          <button type="button" disabled={busy} style={st.secondaryBtn} onClick={() => onSkip && onSkip(item)}>
            Skip
          </button>

          {/* CREATING IS THE LAST BUTTON AND TAKES TWO CLICKS, ON PURPOSE. */}
          {item.kind === REVIEW_KINDS.NEW_PERSON && onCreatePerson && (
            confirmingCreate ? (
              <span style={st.confirmWrap}>
                <span style={st.confirmText}>Add "{item.name}" as a new person?</span>
                <button type="button" disabled={busy} style={st.dangerBtn} onClick={() => run(() => onCreatePerson(item))}>
                  Yes, add to the roster
                </button>
                <button type="button" disabled={busy} style={st.secondaryBtn} onClick={() => setConfirmingCreate(false)}>
                  Cancel
                </button>
              </span>
            ) : (
              <button type="button" disabled={busy} style={st.secondaryBtn} onClick={() => setConfirmingCreate(true)}>
                Add as a new person…
              </button>
            )
          )}
        </div>
      )}
    </li>
  );
}

const st = {
  panel: {
    border: `1px solid ${color.border.default}`,
    borderRadius: radius.md,
    padding: space.md,
    marginBottom: space.md,
    background: color.surface.raised,
  },
  headline: { fontSize: 15, fontWeight: 800, color: color.text.primary, marginBottom: 4 },
  note: { fontSize: 12, color: color.text.muted, margin: "0 0 10px" },
  allClear: { fontSize: 12, fontWeight: 700, color: "#0a7d5e", marginBottom: 8 },
  list: { listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 8 },
  item: {
    border: `1px solid ${color.border.default}`,
    borderRadius: radius.sm,
    padding: "8px 10px",
    background: color.surface.base,
  },
  itemHead: { display: "flex", gap: 8, alignItems: "baseline", flexWrap: "wrap", marginBottom: 4 },
  kind: { fontSize: 10, fontWeight: 800, textTransform: "uppercase", letterSpacing: ".6px", color: color.accent.primary },
  where: { fontSize: 11, color: color.text.muted },
  state: { fontSize: 10, fontWeight: 800, color: "#0a7d5e", textTransform: "uppercase" },
  verbatim: {
    display: "block", fontFamily: "ui-monospace, Menlo, Consolas, monospace",
    fontSize: 12, color: color.text.primary, background: color.surface.raised,
    padding: "4px 6px", borderRadius: 4, overflowWrap: "anywhere",
  },
  understood: { fontSize: 11, color: color.text.secondary, marginTop: 4 },
  why: { fontSize: 11, color: color.text.muted, marginTop: 2 },
  actions: { display: "flex", gap: 6, flexWrap: "wrap", marginTop: 8, alignItems: "center" },
  primaryBtn: {
    border: "none", background: color.accent.primary, color: "#fff", borderRadius: 9999,
    padding: "5px 12px", fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit",
  },
  secondaryBtn: {
    border: `1px solid ${color.border.default}`, background: color.surface.raised,
    color: color.text.secondary, borderRadius: 9999, padding: "5px 12px",
    fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit",
  },
  dangerBtn: {
    border: `1px solid ${color.accent.error}`, background: "transparent", color: color.accent.error,
    borderRadius: 9999, padding: "5px 12px", fontSize: 12, fontWeight: 700,
    cursor: "pointer", fontFamily: "inherit",
  },
  confirmWrap: { display: "inline-flex", gap: 6, alignItems: "center", flexWrap: "wrap" },
  pickLabel: { fontSize: 11, fontWeight: 700, color: color.text.secondary },
  picker: {
    // ⚠️ 16px IS NOT A DESIGN CHOICE. A focusable field below 16px makes iOS
    // Safari zoom the whole page on focus, which reads to a user as the board
    // "moving side to side" — src/pages/mobile/inputZoom.test.js enforces it,
    // and it caught this select at 12px.
    fontSize: 16, padding: "5px 6px", borderRadius: radius.sm,
    border: `1px solid ${color.border.default}`, background: color.surface.base,
    color: color.text.primary, maxWidth: 220,
  },
  confirmText: { fontSize: 11, fontWeight: 700, color: color.accent.error },
  ack: { display: "flex", gap: 8, alignItems: "center", marginTop: 12, fontSize: 12, fontWeight: 700, color: color.text.primary },
};
